import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';   // FW-3A
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { ExpenseAdvanceService } from '../../services/expense-advance.service';
import { ExpenseAdvanceDTO, ExpenseAdvanceStatus } from '../../interfaces/expense.interface';
import { useToast } from '../../context/ToastContext';
import { advanceStatusLabel, claimRef, formatAmount } from '../../utils/expenseDisplay';

const STATUS_COLOR: Record<ExpenseAdvanceStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
    REQUESTED: 'info',
    APPROVED: 'warning',
    PAID: 'primary',
    SETTLED: 'success',
    CANCELLED: 'default',
};

const MyAdvances: React.FC = () => {
    const [advances, setAdvances] = useState<ExpenseAdvanceDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const toast = useToast();

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const page = await ExpenseAdvanceService.listMine();
            setAdvances(page.content);
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load advances');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.showError('Amount must be greater than zero');
            return;
        }
        if (!reason.trim()) {
            toast.showError('Reason is required');
            return;
        }
        setSubmitting(true);
        try {
            await ExpenseAdvanceService.request({ amount: amt, reason: reason.trim() });
            toast.showSuccess('Advance requested');
            setOpen(false);
            setAmount('');
            setReason('');
            load();
        } catch (e: any) {
            toast.showApiError(e, 'Failed to request advance');
        } finally {
            setSubmitting(false);
        }
    };

    const columns: Column<ExpenseAdvanceDTO>[] = [
        { key: 'amount', header: 'Amount', align: 'right', priority: 1, helpKey: 'expense.myAdvances.col.amount', render: a => formatAmount(a.amount) },
        { key: 'reason', header: 'Reason', helpKey: 'expense.myAdvances.col.reason', render: a => a.reason },
        { key: 'status', header: 'Status', helpKey: 'expense.myAdvances.col.status', priority: 1, render: a => <Chip size="small" data-help="expense.myAdvances.col.status" label={advanceStatusLabel(a.status)} color={STATUS_COLOR[a.status]} /> },
        // ---- FW-3A ----
        { key: 'approval', header: 'Approval', helpKey: 'expense.myAdvances.col.approval', render: a => a.approvalRequestId
            ? <RouterLink data-help="expense.myAdvances.action.track" to={`/approvals?request=${a.approvalRequestId}`}>Track approval</RouterLink> : '—' },
        // ---- end FW-3A ----
        { key: 'settled', header: 'Settled against', helpKey: 'expense.myAdvances.col.settled', render: a => a.settlementClaimId ? claimRef(a.settlementClaimId) : '—' },
    ];

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="expense.myAdvances.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">My Cash Advances</Typography>
                <Button variant="contained" data-help="expense.myAdvances.request" onClick={() => setOpen(true)}>Request advance</Button>
            </Stack>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable<ExpenseAdvanceDTO> data-help-section="expense.myAdvances.table"
                    pageKey="expense.myAdvances"
                    columns={columns}
                    rows={advances}
                    getRowKey={a => a.id}
                    emptyMessage="No advances yet."
                />
            )}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Request a cash advance</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField data-help="expense.myAdvances.amount"
                            label="Amount" type="number" value={amount}
                            inputProps={{ min: 0, step: '0.01' }}
                            onChange={e => setAmount(e.target.value)} fullWidth
                        />
                        <TextField data-help="expense.myAdvances.reason"
                            label="Reason" value={reason} onChange={e => setReason(e.target.value)}
                            multiline rows={3} fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button data-help="expense.myAdvances.cancel" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" data-help="expense.myAdvances.submit" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? 'Sending…' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyAdvances;
